import { useCallback, useEffect, useRef, useState } from "react";
import { i18n } from "../i18n";
import {
  BETA_ACCESS_KEY,
  BetaAccessClock,
  type BetaDenialReason,
  betaDenialMessage,
  betaResponseDenialReason,
  GRACE_MS,
} from "./betaAccess";
import { SCHEMA_VERSION } from "./localStore/schema";
import { supabase } from "./supabase";

export function useBetaAccess(expectedUserId?: string) {
  const [clock] = useState(() => {
    let saved = null;
    try {
      saved = localStorage.getItem(BETA_ACCESS_KEY);
    } catch {
      /* Read-only without storage. */
    }
    return new BetaAccessClock(
      saved,
      undefined,
      (value) => localStorage.setItem(BETA_ACCESS_KEY, value),
      () => localStorage.getItem(BETA_ACCESS_KEY),
    );
  });
  const [state, setState] = useState({
    remainingMs: clock.remaining(),
    checking: false,
    hasChecked: Boolean(clock.userId()),
    problem: clock.denialReason() as BetaDenialReason | "connection" | null,
    message: clock.denied() ? betaDenialMessage(clock.denialReason()!) : i18n.t("accessChecking", { ns: "auth" }),
  });
  const running = useRef<AbortController | null>(null);
  const seenRevision = useRef(clock.revision());
  const check = useCallback(async () => {
    if (running.current && !running.current.signal.aborted) return;
    setState((current) => ({ ...current, checking: true }));
    const abort = new AbortController();
    running.current = abort;
    const timeout = window.setTimeout(() => abort.abort(), 10_000);
    const started = performance.now();
    let requestRevision = clock.revision();
    try {
      if (!supabase) throw new Error("未接駁登入服務。");
      const sessionResult = await Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          abort.signal.addEventListener("abort", () => reject(new Error("檢查逾時。")), { once: true }),
        ),
      ]);
      if (sessionResult.error) {
        if ([400, 401, 403].includes(sessionResult.error.status || 0)) clock.deny("sign-in-required");
        throw new Error("未能更新登入狀態；請重新檢查使用權限。");
      }
      const session = sessionResult.data.session;
      if (!session) {
        clock.deny("sign-in-required");
        throw new Error("請重新登入；本地資料仍可查閱及備份。");
      }
      if (expectedUserId && session.user.id !== expectedUserId) throw new Error("登入帳戶已變更，請重新檢查。");
      if (clock.userId() && clock.userId() !== session.user.id) {
        clock.deny("sign-in-required");
        requestRevision = clock.revision();
      }
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/beta-access`, {
        method: "POST",
        cache: "no-store",
        redirect: "error",
        signal: abort.signal,
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY!,
          Authorization: `Bearer ${session.access_token}`,
          "X-Data-Format": String(SCHEMA_VERSION),
        },
        body: "{}",
      });
      if (response.status === 401 || response.status === 403) {
        // A same-account denial takes precedence over a concurrent successful check.
        if (clock.revision() !== requestRevision && clock.savedUserId() !== session.user.id)
          throw new Error("授權狀態已在另一個分頁更新，請重新檢查。");
        // Stop writes before reading the body; legacy or malformed denials still fail closed.
        clock.deny(betaResponseDenialReason(response.status, null), session.user.id);
        const body: unknown = await response.json().catch(() => null);
        if (clock.savedUserId() !== session.user.id) throw new Error("授權狀態已在另一個分頁更新，請重新檢查。");
        const reason = betaResponseDenialReason(response.status, body);
        clock.deny(reason, session.user.id);
        throw new Error(betaDenialMessage(reason));
      }
      if (clock.revision() !== requestRevision) throw new Error("授權狀態已在另一個分頁更新，請重新檢查。");
      if (!response.ok) throw new Error("授權服務暫時未能回應。");
      const result = await response.json();
      if (abort.signal.aborted) throw new Error("檢查已取消。");
      const checked = Date.parse(result.checkedAt);
      const until = Date.parse(result.validUntil);
      const elapsed = performance.now() - started;
      if (
        result.version !== 1 ||
        result.allowed !== true ||
        result.userId !== session.user.id ||
        !Number.isFinite(checked) ||
        !Number.isFinite(until) ||
        until <= checked ||
        until - checked > GRACE_MS ||
        elapsed >= 10_000
      )
        throw new Error("授權回應無效。");
      if (!clock.grant(result.userId, until - checked - elapsed, requestRevision))
        throw new Error("授權狀態已在另一個分頁更新，請重新檢查。");
      // This grant is already rendered here; only another tab's new decision should refresh the message.
      seenRevision.current = clock.revision();
      setState({
        remainingMs: clock.remaining(),
        checking: false,
        hasChecked: true,
        problem: null,
        message: i18n.t("accessConfirmed", { ns: "auth" }),
      });
    } catch (caught) {
      if (abort.signal.aborted && running.current !== abort) return;
      const remainingMs = clock.remaining();
      const denial = clock.denialReason();
      setState({
        remainingMs,
        checking: false,
        hasChecked: true,
        problem: denial || "connection",
        message: denial
          ? betaDenialMessage(denial)
          : caught instanceof TypeError || caught instanceof DOMException
            ? "網絡或授權服務暫時未能連接；請檢查網絡後重試。"
            : caught instanceof Error
              ? caught.message
              : "未能檢查使用權限。",
      });
    } finally {
      clearTimeout(timeout);
      if (running.current === abort) running.current = null;
    }
  }, [clock, expectedUserId]);
  useEffect(() => {
    void check();
    const foreground = () => {
      if (document.visibilityState === "visible") void check();
    };
    const online = () => {
      void check();
    };
    document.addEventListener("visibilitychange", foreground);
    window.addEventListener("focus", online);
    window.addEventListener("online", online);
    const refreshState = () => {
      const remainingMs = clock.remaining();
      const denial = clock.denialReason();
      const revision = clock.revision();
      const updated = revision !== seenRevision.current;
      seenRevision.current = revision;
      setState((current) => ({
        ...current,
        remainingMs,
        problem: denial || (updated && remainingMs > 0 ? null : current.problem),
        hasChecked: current.hasChecked || remainingMs > 0,
        message: denial
          ? betaDenialMessage(denial)
          : updated && remainingMs > 0
            ? i18n.t("accessConfirmed", { ns: "auth" })
            : current.message,
      }));
    };
    const tick = window.setInterval(refreshState, 1000);
    // A long foreground lesson needs successful periodic checks too.
    const refresh = window.setInterval(() => {
      if (document.visibilityState === "visible") void check();
    }, 300_000);
    return () => {
      const previous = running.current;
      running.current = null;
      previous?.abort();
      clearInterval(tick);
      clearInterval(refresh);
      document.removeEventListener("visibilitychange", foreground);
      window.removeEventListener("focus", online);
      window.removeEventListener("online", online);
    };
  }, [check, clock]);
  const assertWritable = useCallback(() => {
    if (!expectedUserId || clock.userId() !== expectedUserId) throw new Error("請先確認目前登入帳戶的使用權限。");
    if (!clock.canWrite()) throw new Error("目前只可讀取。請重新檢查使用權限後再保存；已輸入的內容仍保留。");
  }, [clock, expectedUserId]);
  const clear = useCallback(() => {
    const previous = running.current;
    running.current = null;
    previous?.abort();
    clock.deny();
    localStorage.removeItem(BETA_ACCESS_KEY);
  }, [clock]);
  return {
    ...state,
    // A healthy background check does not need attention. Expiry still blocks writes
    // even while a refresh is pending, and failed checks keep their recovery notice.
    problem: state.problem || (state.hasChecked && state.remainingMs <= 0 ? "connection" : null),
    hasCachedIdentity: Boolean(clock.userId()),
    verifiedUserId: clock.userId(),
    readOnly: state.remainingMs <= 0,
    check,
    assertWritable,
    clear,
  };
}
